import { Maximize2, Check } from "lucide-react";
import type { AdStyle } from "@/feature/ads/mock/adStyles.mock";
import type { Category } from "./types";

interface AdStudioGalleryProps {
  selectedCategory: Category;
  displayedImages: AdStyle[];
  selectedStyleIds: Set<string>;
  categories: { id: Category; label: string }[];
  onCategoryChange: (category: Category) => void;
  onToggleStyle: (style: AdStyle) => void;
}

export function AdStudioGallery({
  selectedCategory,
  displayedImages,
  selectedStyleIds,
  categories,
  onCategoryChange,
  onToggleStyle,
}: AdStudioGalleryProps) {
  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      {/* Instruction */}
      <div className="px-8 py-4 border-b border-gray-100">
        <p className="text-sm text-gray-600">
          Haz clic en un estilo para agregarlo
        </p>
      </div>

      {/* Category Tabs */}
      <div className="px-8 py-4 border-b border-gray-100 flex gap-2 overflow-x-auto">
        {categories.map((category) => (
          <button
            key={category.id}
            type="button"
            onClick={() => onCategoryChange(category.id)}
            className={`px-4 py-2 rounded-lg font-medium text-sm whitespace-nowrap transition-colors ${
              selectedCategory === category.id
                ? "bg-gray-900 text-white"
                : "bg-gray-100 text-gray-700 hover:bg-gray-200"
            }`}
          >
            {category.label}
          </button>
        ))}
      </div>

      {/* Images Grid */}
      <div className="flex-1 overflow-y-auto px-8 py-6">
        <div className="columns-3 gap-2" style={{ columnFill: "balance" }}>
          {displayedImages.map((style) => {
            const isSelected = selectedStyleIds.has(style.id);
            return (
              <div
                key={style.id}
                role="button"
                tabIndex={0}
                onClick={() => onToggleStyle(style)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    onToggleStyle(style);
                  }
                }}
                className={`break-inside-avoid mb-2 relative group cursor-pointer rounded-xl overflow-hidden transition-all duration-200 ${
                  isSelected
                    ? "ring-2 ring-purple-600 ring-offset-2"
                    : "hover:scale-[1.02]"
                }`}
              >
                <div className="relative overflow-hidden w-full">
                  <img
                    src={style.image_url}
                    alt={style.name || "Estilo"}
                    loading="lazy"
                    decoding="async"
                    className="opacity-100 w-full block rounded-xl"
                  />
                </div>
                
                {/* Gradient overlay on hover */}
                <div className="absolute top-0 left-0 right-0 h-12 bg-gradient-to-b from-black/20 to-transparent pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity duration-200 rounded-t-xl" />
                
                {/* Preview button on hover */}
                <button
                  type="button"
                  className="absolute top-2.5 left-2.5 w-6 h-6 bg-white/15 backdrop-blur-md border border-white/20 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-200 hover:bg-white/30 z-10"
                  title="Ver preview"
                  onClick={(e) => {
                    e.stopPropagation();
                  }}
                >
                  <Maximize2 className="w-3 h-3 text-white/90" />
                </button>
                
                {/* Checkmark when selected */}
                {isSelected && (
                  <div className="absolute bottom-2 left-2 w-6 h-6 bg-purple-600 rounded-full flex items-center justify-center z-10 shadow-md">
                    <Check className="h-3.5 w-3.5 text-white stroke-[3]" />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

